// screens-templates.jsx — Rapportmallar manager (create / edit / set standard)
import React from 'react';
import {
  kr, num, fmtMin, fmtHM, STATUS, STATUS_ORDER, ARTICLES, artByCode,
  TEMPLATES, JOBS, VAT, rowAmount, reportTotal, rowLabel, rowUnit, rowRate,
  TODAY, isoLocal, fmtDateLong, fmtDateShort, APP, COMPANY, uid,
  WORK_START_MIN, WORK_END_MIN, hmToMin, minToHM, ceilMin, lunchWindow, lunchAbsorb, groupParts, nextGroupId,
  travelBetween, prevJobSameDay, resolveDeparture,
  Btn, StatusBadge, SecLabel, Card, Avatar, AppHeader, TabBar, TabItem,
  Stepper, Field, Sheet, MapMock, CalendarSheet, Toggle, ProfileButton,
  Icon, ICON_PATHS,
} from '../shared.js';
const { useState: useStateT } = React;

function TemplatesScreen({ templates, setTemplates, defaultId, setDefault, onBack }) {
  const [editing, setEditing] = useStateT(null); // {id?, name, rows[]}
  const [isNew, setIsNew] = useStateT(false);

  const openNew = () => { setEditing({ name: '', rows: ['ARB'] }); setIsNew(true); };
  const openEdit = (t) => { setEditing({ id: t.id, name: t.name, rows: t.rows.slice() }); setIsNew(false); };

  const save = () => {
    const rows = editing.rows.length ? editing.rows : ['ARB'];
    const desc = rows.map(c => artByCode(c).name).join(' · ');
    const name = editing.name.trim() || 'Ny mall';
    if (isNew) {
      const id = 'tpl_' + Date.now();
      setTemplates([...templates, { id, name, desc, rows }]);
    } else {
      setTemplates(templates.map(t => t.id === editing.id ? { ...t, name, desc, rows } : t));
    }
    setEditing(null);
  };
  const removeTpl = () => {
    const rest = templates.filter(t => t.id !== editing.id);
    setTemplates(rest);
    if (defaultId === editing.id && rest[0]) setDefault(rest[0].id);
    setEditing(null);
  };
  const toggleCode = (code) => setEditing(e => ({
    ...e,
    rows: e.rows.includes(code) ? e.rows.filter(c => c !== code) : [...e.rows, code],
  }));

  return (
    <>
      <div className="gv-app">
        <AppHeader onBack={onBack} subtitle="Inställningar" title="Rapportmallar"
          right={<button className="gv-iconbtn" aria-label="Ny mall" onClick={openNew}><Icon name="plus" size={22} stroke={2.2} /></button>} />
        <div className="gv-scroll">
          <Card style={{ marginTop: 4, background: 'var(--surface-2)', fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>
            Sätt upp dina vanligaste rapporter en gång. Din <b style={{ color: 'var(--ink)' }}>standardmall</b> förifylls automatiskt när du rapporterar — sen är det bara att trycka upp tim och belopp.
          </Card>

          <SecLabel action="Ny mall" onAction={openNew}>Dina mallar</SecLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {templates.map(t => {
              const isDef = t.id === defaultId;
              return (
                <Card key={t.id} style={{ border: isDef ? '1.5px solid var(--accent)' : '1px solid var(--line)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} onClick={() => openEdit(t)}>
                    <div className="gv-lrow-ic" style={{ background: isDef ? 'var(--accent-wash)' : 'var(--surface-2)', color: isDef ? 'var(--accent-ink)' : 'var(--ink-2)' }}><Icon name="layers" size={19} stroke={1.9} /></div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 16, fontWeight: 720 }}>{t.name}</span>
                        {isDef && <span style={{ fontSize: 10, fontFamily: 'var(--mono)', fontWeight: 600, color: 'var(--accent-ink)', background: 'var(--accent-wash)', padding: '2px 6px', borderRadius: 5 }}>STANDARD</span>}
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2 }}>{t.rows.length} {t.rows.length === 1 ? 'rad' : 'rader'}</div>
                    </div>
                    <Icon name="edit" size={18} stroke={1.9} color="var(--ink-3)" />
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                    {t.rows.map((c, i) => (
                      <span key={i} className="gv-chip" style={{ height: 26, fontSize: 12, padding: '0 9px' }}>
                        <Icon name={artByCode(c).icon} size={13} stroke={2} color="var(--ink-3)" />{artByCode(c).name}
                      </span>
                    ))}
                  </div>
                  {!isDef && (
                    <button onClick={() => setDefault(t.id)} style={{ marginTop: 12, width: '100%', height: 36, borderRadius: 9, border: '1px solid var(--line-2)', background: 'var(--surface)', color: 'var(--ink-2)', fontWeight: 650, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                      <Icon name="flag" size={15} stroke={2} /> Sätt som standard
                    </button>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* editor */}
      <Sheet open={!!editing} onClose={() => setEditing(null)} title={isNew ? 'Ny mall' : 'Redigera mall'} maxH="88%">
        {editing && <>
          <Field label="Mallens namn" value={editing.name} onChange={(v) => setEditing(e => ({ ...e, name: v }))} placeholder="t.ex. Servicebesök" />
          <div style={{ margin: '16px 0 9px', fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--ink-3)' }}>Rader i mallen</div>
          <div className="gv-list">
            {ARTICLES.map(a => {
              const on = editing.rows.includes(a.code);
              return (
                <div key={a.code} className="gv-lrow is-tap" onClick={() => toggleCode(a.code)}>
                  <div className="gv-lrow-ic" style={on ? { background: 'var(--accent-wash)', color: 'var(--accent-ink)' } : null}><Icon name={a.icon} size={18} stroke={1.9} /></div>
                  <div className="gv-lrow-tt"><b style={{ fontSize: 14.5, fontWeight: 600 }}>{a.name}</b><span className="gv-mono">{a.code} · {a.rate ? kr(a.rate) : 'à-pris'}/{a.unit}</span></div>
                  <div style={{ width: 24, height: 24, borderRadius: 7, border: on ? 'none' : '1.5px solid var(--line-2)', background: on ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {on && <Icon name="check" size={15} stroke={3} color="#fff" />}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
            <Btn variant="primary" icon="check" full onClick={save}>Spara mall</Btn>
            {!isNew && <Btn variant="danger" icon="trash" full onClick={removeTpl}>Ta bort mall</Btn>}
          </div>
        </>}
      </Sheet>
    </>
  );
}

export { TemplatesScreen };
